import { VIEWPORT_HEIGHT, VIEWPORT_WIDTH } from '../../domain/Constants';
import { isVkDesktopPlatform } from '../vk/launchParams';

export type FitScale = { x: number; y: number };

/** Visible host size (iframe-safe: prefer visualViewport / client size). */
export function getHostViewportSize(
  win: Pick<Window, 'innerWidth' | 'innerHeight' | 'visualViewport'> = window,
  docEl: Pick<Element, 'clientWidth' | 'clientHeight'> = document.documentElement,
): { width: number; height: number } {
  const vv = win.visualViewport;
  const width = vv?.width || docEl.clientWidth || win.innerWidth;
  const height = vv?.height || docEl.clientHeight || win.innerHeight;
  return { width, height };
}

/**
 * Desktop / PC host: VK desktop platforms, or a landscape window
 * (VK desktop iframe is wide; phones are portrait).
 */
export function isDesktopHost(
  size: { width: number; height: number } = getHostViewportSize(),
): boolean {
  if (isVkDesktopPlatform()) {
    return true;
  }
  return size.width > size.height;
}

/**
 * Stretch design canvas to exact host width and height (PC and phone).
 * Map/HUD undo non-uniform stretch via `containFitScale` / inv scale where needed.
 */
export function computeFitScale(
  size: { width: number; height: number } = getHostViewportSize(),
  _desktop: boolean = isDesktopHost(size),
): FitScale {
  return {
    x: size.width / VIEWPORT_WIDTH,
    y: size.height / VIEWPORT_HEIGHT,
  };
}

/**
 * Undo `.App` stretch, then shrink uniformly by the smaller fit axis
 * so chrome (HUD, nav) keeps aspect ratio and never overflows the screen.
 */
export function containFitScale(fitScale: FitScale): {
  invX: number;
  invY: number;
  uniform: number;
} {
  const x = fitScale.x > 0 ? fitScale.x : 1;
  const y = fitScale.y > 0 ? fitScale.y : 1;
  return {
    invX: 1 / x,
    invY: 1 / y,
    uniform: Math.min(x, y),
  };
}

/**
 * Undo `.App` stretch, then grow uniformly by the larger fit axis
 * so full-bleed art (level panorama) keeps aspect and covers the screen.
 */
export function coverFitScale(fitScale: FitScale): {
  invX: number;
  invY: number;
  uniform: number;
} {
  const x = fitScale.x > 0 ? fitScale.x : 1;
  const y = fitScale.y > 0 ? fitScale.y : 1;
  return {
    invX: 1 / x,
    invY: 1 / y,
    uniform: Math.max(x, y),
  };
}

/** App fit scale plus the CSS-pixel size of the scaled canvas. */
export type AppFit = {
  scale: FitScale;
  /** Visual size of `.App` after fit (map clip region, CSS px). */
  size: { width: number; height: number };
};

/**
 * Scale applied to the 1080×1920 canvas, and its on-screen size.
 * Map world inverts `scale` so lands/giants stay CSS-pixel sized.
 */
export function computeAppFit(
  size: { width: number; height: number } = getHostViewportSize(),
  desktop: boolean = isDesktopHost(size),
): AppFit {
  const scale = computeFitScale(size, desktop);
  return {
    scale,
    size: {
      width: VIEWPORT_WIDTH * scale.x,
      height: VIEWPORT_HEIGHT * scale.y,
    },
  };
}

/** Portion of the 1080×1920 design canvas still visible after App fit. */
export type VisibleDesignRect = {
  insetX: number;
  insetY: number;
  width: number;
  height: number;
};

/**
 * Design-space rect that remains on-screen after fit.
 * Stretch-to-fill: full canvas maps onto the host.
 */
export function computeVisibleDesignRect(
  size: { width: number; height: number } = getHostViewportSize(),
  _desktop: boolean = isDesktopHost(size),
): VisibleDesignRect {
  void size;
  return {
    insetX: 0,
    insetY: 0,
    width: VIEWPORT_WIDTH,
    height: VIEWPORT_HEIGHT,
  };
}
